/**
 * Client Success → Firms → Client emails (ADR-0115).
 *
 * The review-and-approve surface for a firm's four client-facing email
 * templates (Intro / Update / Accepted / Rejection). A firm proposes wording
 * changes in the portal; LF must approve before they go live. This page shows,
 * per email, the LF `standard`, the firm's current `live` wording, and the
 * firm's `pending` proposal — so approval stops being blind. One click
 * promotes the whole pending diff to live (the existing apply route); there is
 * no deny and no edit-in-CC (ADR-0115 scope).
 *
 * CC-native styling (Tailwind + foreground/muted tokens) — it mirrors the
 * portal's information, not its CSS. Reached from the dashboard review item or
 * the Firm panel's "Client emails" button; the breadcrumb walks back to the
 * roster. Browse mode (nothing pending) shows live vs standard, no button.
 */

import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import type {
  CommsTemplateDiff,
  CommsTemplateFieldKey,
  CommsTemplateKey,
} from "@command-center/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { ClientSuccessNav } from "@/components/client-success-nav";
import { useClientEmails, useApplyCommsTemplates } from "@/lib/use-client-emails";

/** Per-email label + the "when it sends" caption (mirrors CONTEXT.md). */
const EMAIL_META: Record<
  CommsTemplateKey,
  { n: number; label: string; when: string; example: string }
> = {
  intro: {
    example: "client_intro_email",
    n: 1,
    label: "Intro email",
    when: "Sent one full business day after a Lead arrives, if the Firm is opted in.",
  },
  update: {
    example: "client_update_email",
    n: 2,
    label: "Update email",
    when: "Sent on a recurring cadence while the Lead is under review; stops at acceptance, placement or rejection.",
  },
  accepted: {
    example: "client_accepted_email",
    n: 3,
    label: "Accepted email",
    when: "Sent when a handling firm accepts the matter.",
  },
  rejection: {
    example: "client_rejection_email",
    n: 4,
    label: "Rejection email",
    when: "Sent after LegalFlare reviews and approves the rejection.",
  },
};

const FIELD_LABELS: Record<CommsTemplateFieldKey, string> = {
  subject: "Subject",
  body: "Body",
  cc: "CC",
  disabled: "Status",
};

export function ClientEmailsPage() {
  const { id } = useParams({
    from: "/firm-success/firms/$id/client-emails",
  });
  const query = useClientEmails(id);

  return (
    <div className="p-6">
      <ClientSuccessNav active="firms" />

      <nav
        className="mb-3 flex items-center gap-1 text-sm text-plane-muted"
        aria-label="Breadcrumb"
        data-testid="client-emails-breadcrumb"
      >
        <Link
          to="/firm-success/firms"
          className="underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
        >
          Firms
        </Link>
        <span aria-hidden>›</span>
        {query.data ? (
          <Link
            to="/firm-success/firms"
            search={{ firm: id }}
            className="underline decoration-muted-foreground/40 underline-offset-2 hover:decoration-foreground"
            data-testid="client-emails-breadcrumb-firm"
          >
            {query.data.firmName}
          </Link>
        ) : (
          <span>Firm</span>
        )}
        <span aria-hidden>›</span>
        <span className="text-plane-foreground">Client emails</span>
      </nav>

      {query.isLoading ? (
        <div
          className="text-sm text-plane-muted"
          data-testid="client-emails-loading"
        >
          Loading…
        </div>
      ) : query.isError || !query.data ? (
        <div className="text-sm text-error" data-testid="client-emails-error">
          Couldn't load this firm's client emails. It will retry automatically.
        </div>
      ) : (
        <ClientEmailsBody
          firmId={id}
          firmName={query.data.firmName}
          clientCommsEnabled={query.data.clientCommsEnabled}
          hasPending={query.data.hasPending}
          submittedAt={query.data.submittedAt}
          submittedBy={query.data.submittedBy}
          templates={query.data.templates}
        />
      )}
    </div>
  );
}

function ClientEmailsBody({
  firmId,
  firmName,
  clientCommsEnabled,
  hasPending,
  submittedAt,
  submittedBy,
  templates,
}: {
  firmId: string;
  firmName: string;
  clientCommsEnabled: boolean;
  hasPending: boolean;
  submittedAt: string | null;
  submittedBy: string | null;
  templates: CommsTemplateDiff[];
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const apply = useApplyCommsTemplates();

  const onApprove = () => {
    apply.mutate(
      { firmId },
      {
        onSuccess: () => setConfirmOpen(false),
      },
    );
  };

  return (
    <div className="space-y-4" data-testid="client-emails">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl tracking-tight">
            {firmName} · Client emails
          </h1>
          <p className="mt-1 text-sm text-plane-muted">
            {clientCommsEnabled
              ? "Client emails are enabled for this firm."
              : "Client emails are turned off for this firm."}
            {hasPending && submittedBy ? (
              <>
                {" "}
                Pending change submitted by{" "}
                <span className="font-medium text-plane-foreground">
                  {submittedBy}
                </span>
                {submittedAt ? ` on ${submittedAt}` : null}.
              </>
            ) : null}
          </p>
        </div>
        {hasPending ? (
          <Button
            variant="cta"
            onClick={() => setConfirmOpen(true)}
            data-testid="client-emails-approve"
          >
            Approve changes
          </Button>
        ) : null}
      </div>

      {apply.isError ? (
        <div
          className="text-sm text-error"
          data-testid="client-emails-approve-error"
        >
          Couldn't apply the changes. Please try again.
        </div>
      ) : null}

      {hasPending ? null : (
        <p
          className="text-sm text-plane-muted"
          data-testid="client-emails-browse"
        >
          Nothing is pending review. Showing each email's current wording
          against the LegalFlare standard.
        </p>
      )}

      <div className="space-y-3">
        {templates.map((diff) => (
          <EmailCard key={diff.key} diff={diff} hasPending={hasPending} />
        ))}
      </div>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        labelledBy="client-emails-confirm-title"
        describedBy="client-emails-confirm-desc"
        testId="client-emails-confirm"
      >
        <DialogHeader>
          <DialogTitle id="client-emails-confirm-title">
            Approve client-email changes?
          </DialogTitle>
          <DialogDescription id="client-emails-confirm-desc">
            These become the live emails sent to {firmName}'s clients. This
            replaces the current wording and can't be undone from here.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => setConfirmOpen(false)}
            disabled={apply.isPending}
            data-testid="client-emails-confirm-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="cta"
            onClick={onApprove}
            disabled={apply.isPending}
            data-testid="client-emails-confirm-approve"
          >
            {apply.isPending ? "Approving…" : "Approve changes"}
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

function EmailCard({
  diff,
  hasPending,
}: {
  diff: CommsTemplateDiff;
  hasPending: boolean;
}) {
  const meta = EMAIL_META[diff.key];
  const changedSet = new Set(diff.changedFields);

  return (
    <Card className="p-4" data-testid={`client-emails-card-${diff.key}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {meta.n} · {meta.label}
          {" · "}
          <Link
            to="/engineering/glass/communications"
            hash={meta.example}
            className="normal-case tracking-normal underline"
            data-testid={`client-emails-example-${diff.key}`}
          >
            LegalFlare's standard example
          </Link>
        </div>
        <div className="flex items-center gap-2">
          {diff.live.disabled ? (
            <Badge variant="muted">Off</Badge>
          ) : (
            <Badge variant="emerald">Active</Badge>
          )}
          {hasPending && diff.changed ? (
            <Badge variant="amber" data-testid={`client-emails-changed-${diff.key}`}>
              Changed
            </Badge>
          ) : null}
        </div>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{meta.when}</p>

      <dl className="mt-3 space-y-3">
        <FieldRow
          field="subject"
          diff={diff}
          changed={hasPending && changedSet.has("subject")}
        />
        <FieldRow
          field="cc"
          diff={diff}
          changed={hasPending && changedSet.has("cc")}
        />
        <FieldRow
          field="disabled"
          diff={diff}
          changed={hasPending && changedSet.has("disabled")}
        />
        <BodyRow diff={diff} changed={hasPending && changedSet.has("body")} />
      </dl>
    </Card>
  );
}

/** Render a sub-field's value as human text (disabled → On/Off; "" → "—"). */
function fieldText(field: CommsTemplateFieldKey, value: string | boolean): string {
  if (field === "disabled") return value ? "Off" : "Active";
  const s = typeof value === "string" ? value : String(value);
  return s.length > 0 ? s : "—";
}

/** A short field (Subject / CC / Status). Changed → standard vs proposed +
 *  current-live; otherwise just the live value (with standard when it differs). */
function FieldRow({
  field,
  diff,
  changed,
}: {
  field: Exclude<CommsTemplateFieldKey, "body">;
  diff: CommsTemplateDiff;
  changed: boolean;
}) {
  const live = diff.live[field];
  const standard = diff.standard[field];
  const proposed = diff.pending?.[field];

  return (
    <div data-testid={`client-emails-field-${diff.key}-${field}`}>
      <dt className="text-xs font-medium text-muted-foreground">
        {FIELD_LABELS[field]}
      </dt>
      {changed && proposed !== undefined ? (
        <dd className="mt-0.5 space-y-0.5 text-sm">
          <ValueLine label="Proposed" value={fieldText(field, proposed)} strong />
          <ValueLine label="Current" value={fieldText(field, live)} />
          {standard !== live ? (
            <ValueLine label="Standard" value={fieldText(field, standard)} />
          ) : null}
        </dd>
      ) : (
        <dd className="mt-0.5 space-y-0.5 text-sm">
          <ValueLine label="Current" value={fieldText(field, live)} />
          {standard !== live ? (
            <ValueLine label="Standard" value={fieldText(field, standard)} />
          ) : null}
        </dd>
      )}
    </div>
  );
}

/** The Body field — long text, kept behind a native <details> expand. */
function BodyRow({
  diff,
  changed,
}: {
  diff: CommsTemplateDiff;
  changed: boolean;
}) {
  const live = diff.live.body;
  const standard = diff.standard.body;
  const proposed = diff.pending?.body;

  return (
    <div data-testid={`client-emails-field-${diff.key}-body`}>
      <dt className="text-xs font-medium text-muted-foreground">Body</dt>
      <dd className="mt-0.5 space-y-2 text-sm">
        {changed && proposed !== undefined ? (
          <>
            <BodyBlock label="Proposed" value={proposed} strong />
            <BodyBlock label="Current" value={live} />
            {standard !== live ? (
              <BodyBlock label="Standard" value={standard} />
            ) : null}
          </>
        ) : (
          <>
            <BodyBlock label="Current" value={live} />
            {standard !== live ? (
              <BodyBlock label="Standard" value={standard} />
            ) : null}
          </>
        )}
      </dd>
    </div>
  );
}

function ValueLine({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex gap-2">
      <span className="w-16 shrink-0 text-xs text-muted-foreground">
        {label}
      </span>
      <span className={strong ? "font-medium text-foreground" : "text-foreground"}>
        {value}
      </span>
    </div>
  );
}

function BodyBlock({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  const text = value.length > 0 ? value : "—";
  return (
    <details className="rounded border bg-muted/30 px-2 py-1">
      <summary
        className={
          strong
            ? "cursor-pointer text-xs font-medium text-foreground"
            : "cursor-pointer text-xs text-muted-foreground"
        }
      >
        {label} body
      </summary>
      <pre className="mt-1 whitespace-pre-wrap break-words font-sans text-sm text-foreground">
        {text}
      </pre>
    </details>
  );
}
